import { API } from "@/lib/api";
import { withToken } from "@/lib/url";
import { ReadQuestionnaire, WriteQuestionnaire } from "@/types/questionnaire";

// Public Questionnaire API methods
export const getQuestionnaire = async (
  token: string,
): Promise<ReadQuestionnaire> => {
  const response = await fetch(withToken(API.Questionnaire, token));

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    const serverMessage =
      errorBody && typeof errorBody === "object" && "message" in errorBody
        ? (errorBody as { message?: string }).message
        : null;

    throw new Error(
      typeof serverMessage === "string" && serverMessage.trim().length > 0
        ? serverMessage
        : "questionnaires.errors.genericFetchMessage",
    );
  }

  const data = (await response.json()) as {
    data: ReadQuestionnaire;
  };

  if (!data.data || !data.data.questionnaire) {
    return {
      questionnaire: {} as ReadQuestionnaire["questionnaire"],
      attachments: {
        student_id_attachments: [],
        visa_attachments: [],
        polish_card_attachments: [],
        residence_card_attachments: [],
        employment_certificate_attachments: [],
        attachments: [],
      },
      regulations: {},
      can_be_rejected_by_contractor: false,
    };
  }

  return data.data;
};

export const submitQuestionnaire = async (
  agreementId: string,
  token: string,
  data: WriteQuestionnaire,
): Promise<{ pdfData: string }> => {
  const response = await fetch(
    withToken(`${API.Agreements}/${agreementId}/questionnaire`, token),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to submit questionnaire");
  }

  const base64Content = await response.text();
  return { pdfData: base64Content };
};

export const acceptAgreement = async (agreementId: string, token: string) => {
  const response = await fetch(
    withToken(`${API.Agreements}/${agreementId}/accept`, token),
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to accept agreement");
  }
};

export const cancelAgreement = async (agreementId: string, token: string) => {
  const response = await fetch(
    withToken(`${API.Agreements}/${agreementId}/cancel`, token),
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to cancel agreement");
  }
};

export const submitStatements = async (
  agreementId: string,
  token: string,
  data: { rodo_accepted: boolean; regulations_accepted: boolean },
): Promise<void> => {
  const response = await fetch(
    `${API.Agreements}/${agreementId}/statements?token=${token}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to submit statements");
  }
};
